import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Restaurant } from '@shared/types/restaurant';
import { MEITRE_GA_ID } from '@core/config/constants';
import { getAnalyticsEnvironment, ANALYTICS_ENV } from './getEnvironment';
import { loadGA4, loadGTM, loadFacebookPixel } from './scriptLoaders';
import type { AnalyticsContextValue, ConversionData } from './types';
import { createAnalyticsTrackers } from './createAnalyticsTrackers';
import { AnalyticsContext } from './analyticsContext';
export { useAnalyticsContext } from './analyticsContext';

interface AnalyticsProviderProps {
  children: React.ReactNode;
  restaurant: Restaurant;
}

export function AnalyticsProvider({ children, restaurant }: AnalyticsProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const envRef = useRef(getAnalyticsEnvironment());
  const restaurantGaIdRef = useRef(restaurant.googleAnalyticsKey || '');

  // Keep ref in sync if restaurant changes (unlikely but safe)
  restaurantGaIdRef.current = restaurant.googleAnalyticsKey || '';

  useEffect(() => {
    const env = envRef.current;

    if (env === ANALYTICS_ENV.PRODUCTION) {
      // Meitre global GA (no auto page_view — RouteTracker handles it)
      loadGA4(MEITRE_GA_ID, false);

      // Restaurant-specific scripts
      if (restaurant.googleAnalyticsKey) {
        loadGA4(restaurant.googleAnalyticsKey, false);
      }
      if (restaurant.googleTagManagerKey) {
        loadGTM(restaurant.googleTagManagerKey);
      }
      if (restaurant.facebookPixelKey) {
        loadFacebookPixel(restaurant.facebookPixelKey);
      }
    }

    setIsInitialized(true);
  }, [restaurant.googleAnalyticsKey, restaurant.googleTagManagerKey, restaurant.facebookPixelKey]);

  // Shared page_view / event trackers. The factory encapsulates the identical
  // safeCall + LOCAL/PRODUCTION gating; 'v2' tags Meitre GA events and the
  // restaurant GA id drives the per-restaurant page_view double-send.
  // Memoized on stable refs so the context value stays referentially stable.
  const { trackPageView, trackEvent } = useMemo(
    () =>
      createAnalyticsTrackers({
        envRef,
        clientTag: 'v2',
        secondaryGaIdRef: restaurantGaIdRef,
      }),
    [],
  );

  // trackConversion keeps its own safeCall — it stays inline because its
  // reservation_complete + reserva + fbq Schedule shape is provider-specific.
  // Identical defensive wrapper as the factory's: analytics failure must
  // never break the UI (caller may already be in an error path).
  const safeCall = (fn: () => void, context: string): void => {
    try {
      fn();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[Analytics] ${context} threw:`, err);
    }
  };

  const trackConversion = useCallback((data: ConversionData) => {
    const env = envRef.current;

    if (env === ANALYTICS_ENV.LOCAL) {
      console.log('[Analytics] Conversion:', data);
      return;
    }

    if (env === ANALYTICS_ENV.PRODUCTION) {
      if (window.gtag) {
        safeCall(() => {
          window.gtag!('event', 'reservation_complete', {
            send_to: MEITRE_GA_ID,
            client: 'v2',
            reservation_id: data.reservationId,
            unique_id: data.uniqueId,
            party_size: data.diners,
            date: data.date,
            time: data.time,
            guarantee_type: data.guaranteeType,
            fee: data.fee,
            currency: data.currency,
            is_waiting_list: data.isWaitingList ?? false,
          });

          const restaurantGaId = restaurantGaIdRef.current;
          if (restaurantGaId) {
            window.gtag!('event', 'reserva', {
              event_category: 'meitre',
              send_to: restaurantGaId,
            });
          }
        }, 'trackConversion(gtag)');
      }

      if (window.fbq) {
        safeCall(() => {
          window.fbq!('track', 'Schedule');
        }, 'trackConversion(fbq)');
      }
    }
  }, []);

  const value: AnalyticsContextValue = useMemo(() => ({
    trackEvent,
    trackPageView,
    trackConversion,
    isInitialized,
  }), [trackEvent, trackPageView, trackConversion, isInitialized]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

